// Header.jsx (v4.1 — LOGO ONLY: premium glass + compact actions + drawer)
// Keeps your routes + LocalContext webinfo fields intact.

import React, { useEffect, useMemo, useState } from "react";
import "./Header.css";
import { FaPhoneAlt, FaBars, FaTimes } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { useLocalContext } from "../../context/LocalContext";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { webinfo = {} } = useLocalContext();

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const brandName = webinfo.name || "Your Brand";
  const phoneText = webinfo.phone || "+91 80000 00000";

  const phoneHref = webinfo.phonecall
    ? `tel:${webinfo.phonecall}`
    : phoneText
    ? `tel:${phoneText}`
    : "tel:+918000000000";

  const navItems = useMemo(
    () => [
      { label: "Home", to: "/" },
      { label: "Services", to: "/services" },
      { label: "Strategies", to: "/our-strategies" },
      { label: "About", to: "/about" },
      { label: "Process", to: "/process" },
      { label: "Contact", to: "/contact" },
    ],
    []
  );

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  // Close on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // ESC closes drawer
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleNavigate = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const isActive = (to) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  };

  const monogram = (brandName || "B").trim().slice(0, 1).toUpperCase();

  return (
    <header className={`hd4 ${scrolled ? "hd4--scrolled" : ""}`}>
      <div className="hd4-inner">
        {/* Brand (LOGO ONLY) */}
        <button
          className="hd4-brand"
          onClick={() => handleNavigate("/")}
          aria-label={`${brandName} home`}
        >
          <span className="hd4-logoBadge" aria-hidden="true">
            {webinfo.logo ? (
              <img className="hd4-logoImg" src={webinfo.logo} alt={`${brandName} logo`} />
            ) : (
              <span className="hd4-logoMonogram">{monogram}</span>
            )}
          </span>
        </button>

        {/* Desktop nav */}
        <nav className="hd4-nav" aria-label="Main navigation">
          {navItems.map((it) => (
            <button
              key={it.to}
              className={`hd4-link ${isActive(it.to) ? "is-active" : ""}`}
              onClick={() => handleNavigate(it.to)}
            >
              {it.label}
            </button>
          ))}
        </nav>

        {/* Desktop actions */}
        <div className="hd4-actions">
          <a className="hd4-ghost" href={phoneHref} aria-label={`Call ${phoneText}`}>
            <FaPhoneAlt />
            <span>{phoneText}</span>
          </a>

          <button className="hd4-cta" onClick={() => handleNavigate("/contact")}>
            Book a free consult
          </button>

          {/* Mobile burger */}
          <button
            className="hd4-burger"
            onClick={() => setMenuOpen((s) => !s)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div className={`hd4-drawer ${menuOpen ? "is-open" : ""}`} aria-hidden={!menuOpen}>
        <button
          className="hd4-backdrop"
          aria-label="Close menu backdrop"
          onClick={() => setMenuOpen(false)}
        />

        <aside className="hd4-panel" role="dialog" aria-label="Mobile menu">
          <div className="hd4-panelTop">
            {/* Drawer brand (LOGO ONLY) */}
            <div className="hd4-panelBrand">
              <span className="hd4-logoBadge hd4-logoBadge--sm" aria-hidden="true">
                {webinfo.logo ? (
                  <img
                    className="hd4-logoImg"
                    src={webinfo.logo}
                    alt={`${brandName} logo`}
                  />
                ) : (
                  <span className="hd4-logoMonogram">{monogram}</span>
                )}
              </span>
            </div>

            <button className="hd4-x" onClick={() => setMenuOpen(false)} aria-label="Close menu">
              <FaTimes />
            </button>
          </div>

          <nav className="hd4-mnav" aria-label="Mobile navigation">
            {navItems.map((it) => (
              <button
                key={it.to}
                className={`hd4-mitem ${isActive(it.to) ? "is-active" : ""}`}
                onClick={() => handleNavigate(it.to)}
              >
                <span>{it.label}</span>
                <span className="hd4-chevron" aria-hidden="true">
                  →
                </span>
              </button>
            ))}
          </nav>

          <div className="hd4-mactions">
            <a className="hd4-mcall" href={phoneHref}>
              <FaPhoneAlt />
              <span>Call {phoneText}</span>
            </a>

            <button className="hd4-mprimary" onClick={() => handleNavigate("/contact")}>
              Get a quote
            </button>

            <p className="hd4-note">
              Tip: keep your phone in webinfo.phone and optionally webinfo.phonecall.
            </p>
          </div>
        </aside>
      </div>
    </header>
  );
};

export default Header;
